import { Form } from 'antd';
import authApi from 'api/auth';
import { useAppDispatch, useAppTranslation } from 'app/hooks';
import CountDownTimer from 'components/Commons/CountDownTimer';
import VerticalForm from 'components/Form/VerticalForm';
import FieldError from 'components/FormField/FieldError';
import PasswordField from 'components/FormField/PasswordField';
import { showSuccessModal } from 'components/Modals/CommonModals';
import { onChangeAuthPage } from 'features/home/homeSlice';
import { useHandleResponseError } from 'hooks/useHandleResponseError';
import { ResetPasswordFormRequestModel } from 'models/auth';
import React, { useEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { BsEnvelopeCheckFill } from 'react-icons/bs';
import { FaLock } from 'react-icons/fa';
import TextField from '../../../components/FormField/TextField';

interface ResetPasswordPageProps {
  email: string;
  closePage: () => void;
  onChangeLoadingStatus: (value: boolean) => void;
}

const ResetPasswordPage: React.FunctionComponent<ResetPasswordPageProps> = ({
  email,
  closePage,
  onChangeLoadingStatus,
}) => {
  const [form] = Form.useForm();
  const t = useAppTranslation();
  const handleResponseError = useHandleResponseError();
  const dispatch = useAppDispatch();
  const refCapcha = useRef<ReCAPTCHA>(null);
  const [haveError, setHaveError] = useState<boolean>(false);
  const [isValidateCapcha, setIsValidateCapcha] = useState<boolean>(false);

  const handleFieldsChange = () => {
    setHaveError(form.getFieldsError().some((error) => error.errors.length));
  };

  const onSubmit = async (value: ResetPasswordFormRequestModel) => {
    const { confirmPassword, ...rest } = value;
    const request = { ...rest, email: email };

    onChangeLoadingStatus(true);

    const { ok, error } = await authApi.resetPassword(request);

    onChangeLoadingStatus(false);

    if (ok) {
      const onOk = () => {
        form.resetFields();
        closePage();
        dispatch(onChangeAuthPage(0));
      };

      showSuccessModal({
        title: t('resetPassword.success.title'),
        content: t('resetPassword.success.message'),
        onOk,
      });
    }
    if (error) {
      handleResponseError(error);
    }
  };

  useEffect(() => {
    setIsValidateCapcha(Boolean(refCapcha.current?.getValue()));
  }, [refCapcha.current?.getValue()]);

  return (
    <VerticalForm
      className="auth__reset-password"
      name="resetPassword"
      form={form}
      onFinish={onSubmit}
      onFieldsChange={handleFieldsChange}
    >
      <PasswordField
        name="newPassword"
        placeholder={t('placeholder.enterNewPassword')}
        prefix={<FaLock />}
        className="input_primary"
        rules={[
          {
            required: true,
            message: <FieldError message={t('errors.password.required')} />,
          },
          {
            min: 8,
            message: <FieldError message={t('errors.password.invalid')} />,
          },
          {
            max: 50,
            message: <FieldError message={t('errors.password.invalid')} />,
          },
        ]}
      />
      <PasswordField
        name="confirmPassword"
        placeholder={t('placeholder.confirmPassword')}
        prefix={<FaLock />}
        className="input_primary"
        rules={[
          {
            required: true,
            message: <FieldError message={t('register.form.passwordRequired')} />,
          },
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (!value || getFieldValue('newPassword') === value) {
                return Promise.resolve();
              }
              return Promise.reject(<FieldError message={t('register.form.passwordNotMatch')} />);
            },
          }),
        ]}
      />
      <TextField
        name="otp"
        placeholder={t('placeholder.enterOTP')}
        prefix={<BsEnvelopeCheckFill />}
        className="input_primary"
        rules={[
          {
            required: true,
            message: <FieldError message={t('errors.otp.required')} />,
          },
          {
            len: 6,
            message: <FieldError message={t('errors.otp.invalid')} />,
          },
        ]}
      />
      <div className="capcha">
        <ReCAPTCHA ref={refCapcha} sitekey={process.env.REACT_APP_GOOGLE_RECAPTCHA || ''} />
      </div>
      <CountDownTimer isOpen email={email} />
      <div className="login__footer">
        <button
          disabled={haveError || !isValidateCapcha}
          type="submit"
          className={`btn-submit ${haveError || !isValidateCapcha ? 'disabled' : ''}`}
        >
          {t('resetPassword.title')}
        </button>
      </div>
    </VerticalForm>
  );
};

export default ResetPasswordPage;
